import React, { useEffect, useMemo } from "react";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants.ts";
import SingleFile from "@components/FileUpload/SingleFile.tsx";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { DOCUMENTS_TYPE } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import {
  PORT_SERVICE_DOCUMENT_SUMMARY_MAX_CHARS,
  PORT_SERVICE_DOCUMENT_TITLE_MAX_CHARS,
} from "@features/vessel-call/vessel-call-status/PortServices/schema/validators/portServicesDocument.validator";
import { FormProvider, type FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { type PortServicesOutletContext } from "../PortServicesContainer";
import type { PortServicesDocument } from "../store/usePortServicesForm.store";
import { usePortServiceDocumentUpload } from "./utils";

const LANG_BASE = "vessel-call.port-services";
const FIELD_NAME = "attachmentDocuments" as const;
const OTHER_SPECIFY_DOCUMENT_TYPE = DOCUMENTS_TYPE.OTHER_SPECIFY;
const DOCUMENT_TYPE_LABEL_KEYS: Record<
  (typeof DOCUMENTS_TYPE)[keyof typeof DOCUMENTS_TYPE],
  string
> = {
  CERTIFICATE: "certificate",
  PERMIT: "permit",
  DOCUMENT: "document",
  INSURANCE: "insurance",
  PRE_SAILING_MASTER_DECLARATION: "pre-sailing-master-declaration",
  VESSEL_MASTER_SOF: "vessel-master-sof",
  OTHER_SPECIFY: "other-specify",
};

const PortServiceAdditionalDocuments: React.FC = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<PortServicesOutletContext>();
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const { additionalDocuments: uploadConditions } = FILE_UPLOAD_PROFILES;
  const MAX_ADDITIONAL_DOCUMENTS = 25;
  const watchedAttachmentDocumentsRaw = formData.watch(FIELD_NAME) as
    | PortServicesDocument[]
    | undefined;
  const watchedAttachmentDocuments = useMemo(
    () => watchedAttachmentDocumentsRaw ?? [],
    [watchedAttachmentDocumentsRaw],
  );
  const documentTypeOptions = useMemo(
    () =>
      Object.values(DOCUMENTS_TYPE).map((value) => ({
        value,
        label: t(`${LANG_BASE}.document-types.${DOCUMENT_TYPE_LABEL_KEYS[value]}`),
      })),
    [t],
  );

  const { files, setFiles, handleFieldChangeForAzure, removeFileAtIndex, isUploading } =
    usePortServiceDocumentUpload({
      fieldName: FIELD_NAME,
      certificateType: "PORT_SERVICES",
      formData,
      updateFormData,
    });

  useEffect(() => {
    if (files.length) return;

    const storedDocuments = formData.getValues(FIELD_NAME);
    if (!Array.isArray(storedDocuments) || !storedDocuments.length) return;

    setFiles(
      storedDocuments.map((document, idx) => ({
        name: document.name || document.originalFileName || `Document ${idx + 1}`,
        url: document.url,
        type: document.type,
        certificateType: document.certificateType,
        originalFileName: document.originalFileName,
        documentType: document.documentType,
        otherSpecifyType: document.otherSpecifyType,
        documentSummary: document.documentSummary,
        issueDate: document.issueDate,
        expirationDate: document.expirationDate,
      })),
    );
  }, [files.length, formData, setFiles]);

  const allowedExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((ext) =>
        ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  const handleUpload = (key: typeof FIELD_NAME, incomingFiles: File[]): void => {
    if (!incomingFiles?.length) return;
    void handleFieldChangeForAzure(key, incomingFiles);
  };

  /**
   * When user changes document metadata we:
   *  - read the current attachmentDocuments[] from RHF
   *  - patch the specific index & field
   *  - write the full array into the store via updateFormData("attachmentDocuments", ...)
   *
   * RHF itself is already updated by FormItem/Controller; this keeps Zustand in sync.
   */
  const handleAttachmentMetaChange = React.useCallback(
    (key: string, value: unknown) => {
      const match = key.match(
        /^attachmentDocuments\.(\d+)\.(documentType|otherSpecifyType|name|documentSummary|issueDate|expirationDate)$/,
      );
      if (!match) {
        return;
      }

      const index = Number(match[1]);
      const field = match[2] as
        | "documentType"
        | "otherSpecifyType"
        | "name"
        | "documentSummary"
        | "issueDate"
        | "expirationDate";

      const currentDocs =
        (formData.getValues(FIELD_NAME) as PortServicesDocument[] | undefined) ?? [];

      const nextDocs: PortServicesDocument[] = [...currentDocs];

      const existing = nextDocs[index] ?? {
        name: "",
        type: "PORT_SERVICES",
        url: "",
        originalFileName: "",
        otherSpecifyType: "",
        expirationDate: null,
        documentSummary: "",
      };

      if (field === "issueDate" || field === "expirationDate") {
        nextDocs[index] = {
          ...existing,
          [field]: (value as Date) ?? null,
        };
      } else {
        nextDocs[index] = {
          ...existing,
          [field]: (value ?? "") as string,
        };
      }

      if (field === "documentType" && value !== OTHER_SPECIFY_DOCUMENT_TYPE) {
        nextDocs[index] = {
          ...nextDocs[index],
          otherSpecifyType: "",
        };
      }

      updateFormData(FIELD_NAME, nextDocs);
    },
    [formData, updateFormData],
  );
  const validateDrop = (selectedCount: number) => {
    if (files.length + selectedCount > MAX_ADDITIONAL_DOCUMENTS) {
      return t("form-validation.max-images", { max: MAX_ADDITIONAL_DOCUMENTS });
    }
    return null;
  };
  return (
    <FormProvider {...formData}>
      <FormSection isFullWidth title={t(`${LANG_BASE}.fields.additional-documents`)}>
        <div className="rounded-sm relative">
          {isUploading && (
            <div className="absolute inset-0 flex justify-center pointer-events-none top-30 opacity-100">
              <RotateRightIcon
                fontSize="large"
                sx={{
                  "animation": "mui-spin 0.9s linear infinite",
                  "@keyframes mui-spin": { to: { transform: "rotate(360deg)" } },
                }}
              />
            </div>
          )}
          <div className={`${isUploading ? "opacity-50" : ""} p-4`}>
            <FormItem<FieldValues, typeof FIELD_NAME>
              testId="portService-attachment-documents"
              type="file"
              onUpload={handleUpload}
              name={FIELD_NAME}
              uploadConditions={uploadConditions}
              files={files}
              setFiles={setFiles}
              maxFiles={MAX_ADDITIONAL_DOCUMENTS}
              allowedFileExtensions={allowedExtensions}
              disabled={isUploading}
              validateDrop={validateDrop}
              uploadDescription={t("upload.additional-documents-upload", {
                extensions: uploadConditions.format.join(", "),
                size: uploadConditions.maxSizeLabel,
              })}
            />
          </div>
        </div>

        {files.length ? (
          <div className="flex flex-col gap-10 mt-6">
            {files.map((file, idx) => (
              <div
                className="grid w-full gap-4 grid-cols-2"
                key={`${file.name}-${(file as File).lastModified ?? idx}`}
              >
                <div className="col-span-full">
                  <SingleFile
                    testId={`${FIELD_NAME}-${idx}`}
                    file={file}
                    currentFileIndex={idx}
                    fieldName={FIELD_NAME}
                    setFiles={() => removeFileAtIndex(idx)}
                  />
                </div>
                <FormItem<FieldValues, `attachmentDocuments.${number}.documentType`>
                  testId={`additional-documents-${idx}-document-type`}
                  name={`attachmentDocuments.${idx}.documentType`}
                  type="select"
                  options={documentTypeOptions}
                  label={t(`${LANG_BASE}.fields.document-type`)}
                  onFieldChange={handleAttachmentMetaChange}
                />
                {watchedAttachmentDocuments[idx]?.documentType === OTHER_SPECIFY_DOCUMENT_TYPE ? (
                  <FormItem<FieldValues, `attachmentDocuments.${number}.otherSpecifyType`>
                    testId={`additional-documents-${idx}-other-specify`}
                    name={`attachmentDocuments.${idx}.otherSpecifyType`}
                    type="text"
                    label={t(`${LANG_BASE}.fields.other-specify`)}
                    maxChars={30}
                    onFieldChange={handleAttachmentMetaChange}
                  />
                ) : (
                  <div className="hidden lg:block" aria-hidden="true" />
                )}
                <div className="col-span-full">
                  <FormItem<FieldValues, `attachmentDocuments.${number}.name`>
                    testId={`additional-documents-${idx}-document-name`}
                    name={`attachmentDocuments.${idx}.name`}
                    type="text"
                    label={t(`${LANG_BASE}.fields.document-title`)}
                    maxChars={PORT_SERVICE_DOCUMENT_TITLE_MAX_CHARS}
                    onFieldChange={handleAttachmentMetaChange}
                  />
                </div>
                <FormItem<FieldValues, `attachmentDocuments.${number}.issueDate`>
                  testId={`additional-documents-${idx}-issue-date`}
                  name={`attachmentDocuments.${idx}.issueDate`}
                  type="date-picker"
                  label={t(`${LANG_BASE}.fields.issue-date`)}
                  onFieldChange={handleAttachmentMetaChange}
                  timePicker={false}
                  dateConstraint="not-future"
                />
                <FormItem<FieldValues, `attachmentDocuments.${number}.expirationDate`>
                  testId={`additional-documents-${idx}-expiry-date`}
                  name={`attachmentDocuments.${idx}.expirationDate`}
                  type="date-picker"
                  label={t(`${LANG_BASE}.fields.expiration-date`)}
                  onFieldChange={handleAttachmentMetaChange}
                  timePicker={false}
                  dateConstraint="not-past"
                />
                <div className="col-span-full">
                  <FormItem<FieldValues, `attachmentDocuments.${number}.documentSummary`>
                    testId={`additional-documents-${idx}-document-summary`}
                    name={`attachmentDocuments.${idx}.documentSummary`}
                    type="text"
                    label={t(`${LANG_BASE}.fields.document-summary`)}
                    maxChars={PORT_SERVICE_DOCUMENT_SUMMARY_MAX_CHARS}
                    onFieldChange={handleAttachmentMetaChange}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </FormSection>
    </FormProvider>
  );
};

export default PortServiceAdditionalDocuments;
